import { useMemo } from 'react';

import TransactionItem from './Item';
import CustomTransactionItem from './Item/Custom';
import DoubleTransactionItem from './Item/DoubleItem';

import './index.css';
import LoadingIcons from 'react-loading-icons';
import ComplexParamsBundleComponent from './Item/ComplexParamsBundle';
import LegacyIndicator from './Item/LegacyIndicator';
import LatestBotStateComponent from './Item/LatestBotState';
import {
  DetailCompactTransaction,
  Subscription,
} from '../../../../../types/subscription';
import { CustomTransaction } from 'grts-utils/src/types/Transaction';
import { ComplexParamsBundle, LatestBotState } from '../../../../Detail/types';
import {
  CbrtRangeState,
  MrcState,
  ProfitResultIndicatorConfigs,
  WakeupTimes,
} from '../../../../ProfitCalculations/types';
import { Bot } from 'grts-utils/src/types/Bot';
import { compareComplexParamsBundle } from '../../../../../helpers/bundleTransactionsToCompactForm';

interface Props {
  transactions: (DetailCompactTransaction | CustomTransaction)[] | undefined;
  isLoading: boolean;
  compactTransactionLength?: number | undefined;
  currentComplexParams?: ComplexParamsBundle;
  transactionsByTimestamp?: Record<string, DetailCompactTransaction[]>;
  cbrtRangeStates?: CbrtRangeState[];
  mrcStates?: MrcState[] | undefined;
  wakeupTimes?: WakeupTimes;
  bot?: Bot;
  indicatorConfig?: ProfitResultIndicatorConfigs;
  subscription: Subscription;
}

const isCustomTransaction = (
  transaction: DetailCompactTransaction | CustomTransaction,
): transaction is CustomTransaction =>
  transaction.transactionType.indexOf('_BALANCE') !== -1;

const isTransaction = (
  item: DetailCompactTransaction | ComplexParamsBundle | CustomTransaction,
): item is DetailCompactTransaction => item.hasOwnProperty('transactionType');

const isBotLatestState = (
  item:
    | DetailCompactTransaction
    | ComplexParamsBundle
    | LatestBotState
    | CustomTransaction,
): item is LatestBotState => item.hasOwnProperty('cbrtRangeStates');

const isComplexParamsBundle = (
  item:
    | DetailCompactTransaction
    | ComplexParamsBundle
    | LatestBotState
    | CustomTransaction,
): item is ComplexParamsBundle =>
  Object.keys(item).length === 2 &&
  item.hasOwnProperty('complexParams') &&
  item.hasOwnProperty('timeFrames');

const TransactionList: React.FC<Props> = (props: Props) => {
  const {
    transactions,
    isLoading,
    compactTransactionLength,
    currentComplexParams,
    cbrtRangeStates,
    mrcStates,
    wakeupTimes,
    bot,
    indicatorConfig,
    subscription,
  } = props;

  const items = useMemo(() => {
    const compactTransactions = transactions;
    // const takeProfitTransactions = []
    // transactions?.allTransactions.filter((t) =>
    //   t.transactionType.includes('TAKE_PROFIT'),
    // ) || [];

    if (!compactTransactions?.length) {
      return [];
    }

    const items: Array<
      | string
      | ComplexParamsBundle
      | LatestBotState
      | DetailCompactTransaction
      | CustomTransaction
      | DetailCompactTransaction[]
    > = [];

    if (currentComplexParams) {
      items.push(currentComplexParams);
    }

    /** Push latest state */
    // if (latestBotState) {
    //   items.push(latestBotState);
    // }

    let complexParamsBundle = currentComplexParams;
    let isLegacyPushed = false;

    // if (isDoubleView && transactionsByTimestamp) {
    //   /** Use transactionsByTimestamp */
    //   const entries = Object.entries(transactionsByTimestamp).sort(
    //     (a, b) => parseFloat(b[0]) - parseFloat(a[0]),
    //   );
    //   data = entries.map((e) => e[1]);
    // } else {
    const data = [...compactTransactions].sort(
      (a, b) => b.transactionTimestamp - a.transactionTimestamp,
    );
    // }

    for (let i = 0; i < data.length; i++) {
      const datum = data[i];
      let transaction: DetailCompactTransaction | CustomTransaction;

      if (Array.isArray(datum)) {
        transaction = datum[0];
      } else {
        transaction = datum;
      }

      if (
        !complexParamsBundle &&
        !isCustomTransaction(transaction) &&
        !!transaction.complexParams &&
        !!transaction.timeFrames
      ) {
        complexParamsBundle = {
          complexParams: transaction.complexParams,
          timeFrames: transaction.timeFrames,
        };

        items.push(complexParamsBundle);
        items.push(datum);
        continue;
      }

      if (!complexParamsBundle) {
        if (!isLegacyPushed) {
          items.push('LEGACY');
          isLegacyPushed = true;
        }

        items.push(datum);
        continue;
      }

      /** If compact transaction has different complex params bundle
       * add that param bundle first
       */

      if (
        !isCustomTransaction(transaction) &&
        (!transaction.complexParams || !transaction.timeFrames)
      ) {
        if (!isLegacyPushed) {
          items.push('LEGACY');
          isLegacyPushed = true;
        }

        items.push(datum);
        continue;
      }

      const hasSameComplexParamsBundle = compareComplexParamsBundle(
        complexParamsBundle,
        transaction as DetailCompactTransaction,
      );

      if (hasSameComplexParamsBundle) {
        items.push(datum);
        continue;
      }

      if (
        !isCustomTransaction(transaction) &&
        transaction.complexParams &&
        transaction.timeFrames
      ) {
        complexParamsBundle = {
          complexParams: transaction.complexParams,
          timeFrames: transaction.timeFrames,
        };

        items.push(complexParamsBundle);
      }

      items.push(datum);
    }

    return items;
  }, [currentComplexParams, transactions]);

  const compactTransactionWithoutCustomTransactions = useMemo(() => {
    return transactions
      ? (([...transactions]
          ?.filter((t) => !isCustomTransaction(t))
          .sort((a, b) => a.transactionTimestamp - b.transactionTimestamp) ??
          []) as DetailCompactTransaction[])
      : [];
  }, [transactions]);

  const renderTransactionItem = (
    item:
      | DetailCompactTransaction
      | CustomTransaction
      | DetailCompactTransaction[]
      | ComplexParamsBundle
      | LatestBotState
      | string,
    index: number,
  ) => {
    if (!transactions) {
      return null;
    }

    if (typeof item === 'string') {
      return <LegacyIndicator />;
    }

    // if (Array.isArray(item) && item.length === 2 && profitCalcsTransactions) {
    //   const realTransactionIndex =
    //     compactTransactionWithoutCustomTransactions?.findIndex(
    //       (t) => t.compactTransactionId === item[0].compactTransactionId,
    //     ) ?? 0;

    //   const realClusteredTransactions = transactions?.allTransactions.filter(
    //     (t) => t.compactTransactionId === item[0].compactTransactionId,
    //   );
    //   const profitClusteredTransactions = profitCalcsTransactions
    //     .filter(
    //       (t: any) => t.compactTransactionId === item[1].compactTransactionId,
    //     )
    //     .sort((a, b) => b.transactionTimestamp - a.transactionTimestamp);

    //   const clusteredTransactions = realClusteredTransactions.map((t, i) => [
    //     t,
    //     profitClusteredTransactions[i],
    //   ]);

    //   return (
    //     <DoubleTransactionItem
    //       key={index}
    //       index={index}
    //       transactions={item}
    //       compactTransactionIndex={realTransactionIndex}
    //       currentProfit={transactions.currentProfit?.currentProfit}
    //       currentPriceChange={transactions.currentProfit?.currentPriceChange}
    //       profitCalcsCurrentProfit={profitCalcsCurrentProfit}
    //       compactTransactionsLength={compactTransactionLength || 0}
    //       clusteredTransactions={clusteredTransactions}
    //     />
    //   );
    // }

    const transaction = Array.isArray(item) ? item[0] : item;

    if (isBotLatestState(transaction)) {
      return null;
      //   return (
      //     <LatestBotStateComponent
      //       latestBotState={transaction}
      //       complexParamsChangeTimestamps={complexParamsChangeTimestamps}
      //     />
      //   );
    }

    if (isComplexParamsBundle(transaction)) {
      return (
        <ComplexParamsBundleComponent
          complexParamsBundle={transaction}
          isFirst={index === 0}
        />
      );
    }

    if (isCustomTransaction(transaction)) {
      return (
        <CustomTransactionItem
          key={index}
          transaction={transaction}
          index={index}
          totalLength={items?.length}
        />
      );
    }

    // No TPT yet
    // if (
    //   (transaction as DetailCompactTransaction).transactionType.includes(
    //     'TAKE_PROFIT',
    //   )
    // ) {
    //   // compactTransactions are sorting from new to old
    //   const { previousCompactTransaction, nextCompactTransaction } =
    //     compactTransactionWithoutCustomTransactions.reduce<{
    //       previousCompactTransaction: DetailCompactTransaction | null;
    //       nextCompactTransaction: DetailCompactTransaction | null;
    //     }>(
    //       (acc, curr) => {
    //         if (acc.nextCompactTransaction && acc.previousCompactTransaction) {
    //           return acc;
    //         }

    //         if (curr.transactionTimestamp > transaction.transactionTimestamp) {
    //           acc.nextCompactTransaction = curr;
    //         }

    //         if (curr.transactionTimestamp < transaction.transactionTimestamp) {
    //           acc.previousCompactTransaction = curr;
    //         }

    //         return acc;
    //       },
    //       { previousCompactTransaction: null, nextCompactTransaction: null },
    //     );

    //   // prev and next
    //   const compactTransactions: DetailCompactTransaction[] = [];

    //   if (previousCompactTransaction) {
    //     compactTransactions.push(previousCompactTransaction);
    //   }

    //   if (nextCompactTransaction) {
    //     compactTransactions.push(nextCompactTransaction);
    //   }

    //   const startTimestamp =
    //     transaction.candles?.multi?.[0]?.startTimestamp ?? 0;
    //   const endTimestamp =
    //     transaction.candles?.multi?.[transaction.candles?.multi?.length - 1]
    //       ?.endTimestamp ?? 0;

    //   const cpt = compactTransactionWithoutCustomTransactions.filter(
    //     (t) =>
    //       startTimestamp <= t.transactionTimestamp &&
    //       t.transactionTimestamp <= endTimestamp,
    //   );

    //   cpt.push(transaction);

    //   return (
    //     <TransactionItem
    //       key={index}
    //       index={index}
    //       bot={bot}
    //       transaction={transaction}
    //       compactTransactionIndex={null}
    //       currentProfit={undefined} // @TODO use this from subscription
    //       currentPriceChange={undefined}
    //       compactTransactionsLength={0}
    //       clusteredTransactions={[transaction]}
    //       compactTransactions={cpt as unknown as DetailCompactTransaction[]}
    //       showCandles={true}
    //       cbrtRangeStates={cbrtRangeStates?.filter((s) =>
    //         transaction?.candles && transaction?.candles?.higher?.length > 0
    //           ? transaction.candles.higher[0].startTimestamp <= s.timestamp &&
    //             s.timestamp <=
    //               transaction.candles.higher[
    //                 transaction.candles.higher.length - 1
    //               ].endTimestamp
    //           : 0,
    //       )}
    //       mrcStates={mrcStates?.filter((s) =>
    //         transaction?.candles && transaction?.candles?.higher?.length > 0
    //           ? transaction.candles.higher[0].startTimestamp <=
    //               s.startTimestamp &&
    //             s.startTimestamp <=
    //               transaction.candles.higher[
    //                 transaction.candles.higher.length - 1
    //               ].endTimestamp
    //           : 0,
    //       )}
    //       timeFrames={currentComplexParams?.timeFrames}
    //       showCurrentProfit={true}
    //       wakeupTimes={wakeupTimes}
    //       indicatorConfig={indicatorConfig}
    //     />
    //   );
    // }

    const compactTransaction = transaction as DetailCompactTransaction;

    const compactTransactionId = compactTransaction.compactTransactionId;
    const rootIndex =
      [...compactTransactionWithoutCustomTransactions]
        ?.sort((a, b) => b.transactionTimestamp - a.transactionTimestamp)
        ?.findIndex(
          (t) =>
            !!t.compactTransactionId &&
            t.compactTransactionId.toString() === compactTransactionId,
        ) ?? 0;

    const compactTransactionList = compactTransactionWithoutCustomTransactions
      ? [...compactTransactionWithoutCustomTransactions].sort(
          (a, b) => b.transactionTimestamp - a.transactionTimestamp,
        )
      : [];
    const compactTransactions = [];

    if (rootIndex !== 0) {
      compactTransactions.push(compactTransactionList[rootIndex - 1]);
    }

    compactTransactions.push(compactTransactionList[Math.max(0, rootIndex)]);

    compactTransactions.push(
      compactTransactionList[
        Math.min(
          rootIndex + 1,
          compactTransactionWithoutCustomTransactions?.length
            ? compactTransactionWithoutCustomTransactions.length - 1
            : 0,
        )
      ],
    );

    const clusteredTransactions = [
      ...compactTransactionWithoutCustomTransactions,
    ]
      .filter(
        (t) =>
          t.compactTransactionId === compactTransaction.compactTransactionId,
      )
      .sort((a, b) => b.transactionTimestamp - a.transactionTimestamp);

    return (
      <TransactionItem
        key={index}
        index={index}
        bot={bot}
        transaction={compactTransaction}
        compactTransactionIndex={
          compactTransactionWithoutCustomTransactions?.findIndex(
            (t) =>
              t.compactTransactionId ===
              compactTransaction.compactTransactionId,
          ) ?? 0
        }
        currentProfit={subscription.currentProfit}
        currentPriceChange={subscription.currentPriceChange}
        compactTransactionsLength={compactTransactionLength || 0}
        clusteredTransactions={clusteredTransactions}
        compactTransactions={
          compactTransactions as unknown as DetailCompactTransaction[]
        }
        showCandles={true}
        cbrtRangeStates={cbrtRangeStates?.filter((s) =>
          compactTransaction?.candles &&
          compactTransaction?.candles?.higher?.length > 0
            ? compactTransaction.candles.higher[0].startTimestamp <=
                s.timestamp &&
              s.timestamp <=
                compactTransaction.candles.higher[
                  compactTransaction.candles.higher.length - 1
                ].endTimestamp
            : 0,
        )}
        mrcStates={mrcStates?.filter((s) =>
          compactTransaction?.candles &&
          compactTransaction?.candles?.higher?.length > 0
            ? compactTransaction.candles.higher[0].startTimestamp <=
                s.startTimestamp &&
              s.startTimestamp <=
                compactTransaction.candles.higher[
                  compactTransaction.candles.higher.length - 1
                ].endTimestamp
            : 0,
        )}
        timeFrames={currentComplexParams?.timeFrames}
        showCurrentProfit={
          compactTransactionWithoutCustomTransactions?.findIndex(
            (t) =>
              t.compactTransactionId ===
              compactTransaction.compactTransactionId,
          ) ===
          compactTransactionWithoutCustomTransactions.length - 1
        }
        wakeupTimes={wakeupTimes}
        indicatorConfig={indicatorConfig}
      />
    );
  };

  if (!transactions) {
    return (
      <span className="detail-transactions-no-transactions-found">
        No transactions found
      </span>
    );
  }

  return (
    <div className="detail-transactions-container">
      {items.map(renderTransactionItem)}
    </div>
  );
};

export default TransactionList;
